import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const SalesChat = ({ 
  // Props to control where the chat appears
  allowedPaths = ['/'],  // Default to landing page only
  excludedPaths = [],     // Pages to explicitly exclude
  showOnAllPages = false,  // Override to show everywhere
  labelText = "Recruit with us!"  // Custom label text
}) => {
  const location = useLocation();
  const [showLabel, setShowLabel] = useState(false);

  useEffect(() => {
    // Determine if chat should be shown on current page
    const shouldShowChat = () => {
      // If showing on all pages, check excludes only
      if (showOnAllPages) {
        return !excludedPaths.includes(location.pathname);
      }
      
      // Otherwise, check if current path is in allowed list
      // Support both exact matches and pattern matching
      return allowedPaths.some(path => {
        if (path.endsWith('*')) {
          // Pattern matching (e.g., '/recruiter/*' matches all recruiter pages)
          return location.pathname.startsWith(path.slice(0, -1));
        }
        return location.pathname === path;
      });
    };

    if (!shouldShowChat()) {
      // Hide sales chat button and label if they exist
      const salesButton = document.querySelector('[data-button-id="kc78lcvq"]');
      if (salesButton) {
        // Use visibility instead of display to preserve LiveAgent's state
        salesButton.style.visibility = 'hidden';
        salesButton.style.pointerEvents = 'none';
      }
      setShowLabel(false);
      return;
    }
    
    // Show the label when chat should be shown
    setShowLabel(true);

    // Check if LiveAgent is already loaded (from HelpDesk component)
    if (window.LiveAgent && window.LiveAgent.createButton) {
      // Check if sales button already exists in DOM
      const existingButton = document.querySelector('[data-button-id="kc78lcvq"]');
      
      if (!existingButton) {
        // Button doesn't exist, create it
        const scriptElement = document.getElementById('la_sales_chat');
        if (!scriptElement) {
          // Create a dummy script element for LiveAgent to reference
          const dummyScript = document.createElement('script');
          dummyScript.id = 'la_sales_chat';
          document.body.appendChild(dummyScript);
          window.LiveAgent.createButton('kc78lcvq', dummyScript);
        } else {
          // Script exists but button doesn't, recreate button
          window.LiveAgent.createButton('kc78lcvq', scriptElement);
        }
      } else {
        // Button exists, just show it
        existingButton.style.visibility = 'visible';
        existingButton.style.pointerEvents = 'auto';
      }
      return;
    }

    // Load LiveAgent script (if not already loaded by HelpDesk)
    const script = document.createElement('script');
    script.id = 'la_sales_chat';
    script.defer = true;
    script.src = 'https://nacme.ladesk.com/scripts/track.js';
    
    script.onload = script.onreadystatechange = function() {
      const rs = this.readyState;
      if (rs && (rs !== 'complete') && (rs !== 'loaded')) {
        return;
      }
      
      // Initialize Sales Chat button after script loads
      if (window.LiveAgent && window.LiveAgent.createButton) {
        window.LiveAgent.createButton('kc78lcvq', this);
      }
    };
    
    // Insert the script into the document
    document.body.appendChild(script);

    // Cleanup
    return () => {
      // Hide the sales chat and label when navigating away from allowed pages
      const salesButton = document.querySelector('[data-button-id="kc78lcvq"]');
      if (salesButton && !shouldShowChat()) {
        salesButton.style.visibility = 'hidden';
        salesButton.style.pointerEvents = 'none';
      }
      if (!shouldShowChat()) {
        setShowLabel(false);
      }
    };
  }, [location.pathname, allowedPaths, excludedPaths, showOnAllPages]);

  // Render the label above the chat button
  return showLabel ? (
    <div 
      style={{
        position: 'fixed',
        bottom: '120px',  // Adjust based on your button position
        right: '20px',    // Match the button's right position
        backgroundColor: '#2563eb',  // Blue background
        color: 'white',
        padding: '8px 16px',
        borderRadius: '20px',
        fontSize: '14px',
        fontWeight: '600',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        zIndex: 999998,  // Just below the chat button's z-index
        animation: 'pulse 2s infinite',
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'transform 0.2s',
      }}
      onClick={() => {
        // Click the actual chat button when label is clicked
        const chatButton = document.querySelector('[data-button-id="kc78lcvq"]');
        if (chatButton) {
          chatButton.click();
        }
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.05)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
      }}
    >
      {labelText}
      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  ) : null;
};

export default SalesChat;